import React, {useEffect, useState, useRef} from 'react';
import {observer} from 'mobx-react';
import {
    Button,
    CheckBox,
    Flex,
    RadioButton,
    Text,
    Title,
    Popover,
    PopoverPlacement,
    HTMLDialog
} from '@priority-software/priority-style-react';
import {ToggleButton} from '../ToggleButton';
import {CheckedValue} from '../ToggleButton/enum';
import {ModernTooltip} from '../Common/Tooltip/ModernTooltip';
import {MAILBOXSEND_FORM_NAME} from './constants';
import {saveMailboxFeedback} from './actions/consent';
import {getTopDialogZindex} from '../../_shared/GWTBridge/invoke/priModalDlg';
import texts, {getServerTexts} from '../../features/Form/components/SidePanelForms/texts';
import {Form} from '../../stores/Form';
import {isRTL} from '../../Localization';
import {mailboxSendToggleStore} from './store';
import styles from './styles.module.scss';

type Props = {
    currentForm: Form;
    componentClasses?: string;
    showMailboxTogglePopup?: boolean;
    setShowMailboxTogglePopup?: (show: boolean) => void;
    onToggleChange: () => Promise<void>;
};

export const MailboxSendToggle = observer(
    ({
        currentForm,
        componentClasses,
        showMailboxTogglePopup,
        onToggleChange,
        setShowMailboxTogglePopup
    }: Props) => {
        const toggleState = mailboxSendToggleStore.uiState;
        const [showDialog, setShowDialog] = useState(false);
        const [feedbackReason, setFeedbackReason] = useState<string>('');
        const [feedbackDetails, setFeedbackDetails] = useState<string>('');
        const [contactMe, setContactMe] = useState<boolean>(false);

        const [textsLoaded, setTextsLoaded] = useState(false);
        const toggleButtonRef = useRef<HTMLDivElement>(null);

        useEffect(() => {
            getServerTexts().then(() => {
                setTextsLoaded(true);
            });
        }, []);

        // Check if this is MAILBOXSEND form with no parent
        const shouldShowToggle =
            currentForm && !currentForm.parentForm && currentForm.ename === MAILBOXSEND_FORM_NAME;

        const handleToggleChange = async (value: CheckedValue) => {
            if (toggleState !== value) {
                // Show dialog only when switching FROM NEW (right) TO CLASSIC (left)
                if (toggleState === CheckedValue.right && value === CheckedValue.left) {
                    setShowDialog(true);
                } else {
                    // For all other cases, proceed with the change
                    mailboxSendToggleStore.toggleUIState(value);
                    await onToggleChange?.();
                }
            }
        };

        const handleBackToClassic = async () => {
            // Save feedback data if any is provided
            if (feedbackReason || feedbackDetails || contactMe) {
                await saveMailboxFeedback({
                    type: 'UI_Toggle',
                    reason: feedbackReason,
                    free_text: feedbackDetails,
                    consent: contactMe ? 'Y' : 'N'
                });
            }

            setShowDialog(false);
            setFeedbackReason(''); // Reset feedback reason
            setFeedbackDetails(''); // Reset feedback details
            setContactMe(false); // Reset contact preference
            onToggleChange();
        };

        const handleStayWithNew = () => {
            setShowDialog(false);
            setFeedbackReason(''); // Reset feedback reason
            setFeedbackDetails(''); // Reset feedback details
            setContactMe(false); // Reset contact preference
            // Don't change the toggle state, stay in NEW
        };

        if (!shouldShowToggle || !textsLoaded) {
            return null;
        }

        return (
            <>
                <ModernTooltip tooltip={texts.toggle_tooltip} position="bottom">
                    <div ref={toggleButtonRef}>
                        <ToggleButton
                            checked={toggleState}
                            onChange={handleToggleChange}
                            left={texts.classic?.toUpperCase()}
                            right={texts.new?.toUpperCase()}
                            size="small"
                            componentClasses={componentClasses}
                            itemWidth={'fit-content'}
                        />
                    </div>
                </ModernTooltip>

                <Popover
                    zIndex={getTopDialogZindex()}
                    placement={PopoverPlacement.bottom}
                    show={showMailboxTogglePopup}
                    anchor={toggleButtonRef.current}
                    rtl={isRTL()}
                    clickable
                    closable
                    escClosable
                    outsideClickClose={true}
                    onClose={() => {
                        setShowMailboxTogglePopup(false);
                    }}
                    hoverable={false}
                    content={
                        <div style={{padding: '16px', minWidth: '200px'}}>
                            <Text skin="paragraph-2">{texts.promo_popover_text}</Text>
                        </div>
                    }
                />

                {showDialog ? (
                    <HTMLDialog
                        isOpen={showDialog}
                        onClose={handleStayWithNew}
                        zIndex={getTopDialogZindex()}
                        maskClosable={false}
                        width={640}
                        closable={false}
                        content={
                            <Flex
                                direction="column"
                                alignItems="center"
                                className={styles.dialogContent}
                            >
                                <Flex
                                    direction="column"
                                    alignItems="flex-start"
                                    style={{width: '100%', gap: 8}}
                                >
                                    <Title skin="h2" textColor="primary-700">
                                        {texts.feedback_title}
                                    </Title>
                                    <Text textColor="primary-700" skin="paragraph-1">
                                        {texts.feedback_content}
                                    </Text>
                                    <div className={styles.radioButtonContainer}>
                                        <RadioButton
                                            selected={feedbackReason}
                                            onChange={setFeedbackReason}
                                            options={[
                                                {
                                                    label: texts.feedback_reason1,
                                                    value: 'missing_function'
                                                },
                                                {label: texts.feedback_reason2, value: 'other'}
                                            ]}
                                            border={false}
                                        />
                                    </div>
                                    <Text textColor="primary-700" skin="paragraph-1">
                                        {texts.feedback_reason3}
                                    </Text>
                                    <textarea
                                        value={feedbackDetails}
                                        onChange={e => setFeedbackDetails(e.target.value)}
                                        onKeyDown={e => e.stopPropagation()}
                                        onKeyUp={e => e.stopPropagation()}
                                        placeholder={texts.feedback_reason3_placeholder}
                                        className={styles.feedbackTextarea}
                                    />
                                    <CheckBox
                                        checked={contactMe}
                                        onChange={() => setContactMe(!contactMe)}
                                        children={texts.feedback_contact_me}
                                        componentClasses={styles.checkbox}
                                    />
                                </Flex>
                            </Flex>
                        }
                        footer={
                            <Flex justifyContent="space-between">
                                <Button skin="secondary" onClick={handleStayWithNew}>
                                    {texts.feedback_stay_new}
                                </Button>
                                <Button
                                    skin="primary"
                                    onClick={handleBackToClassic}
                                    disabled={!feedbackReason}
                                >
                                    {texts.feedback_to_classic}
                                </Button>
                            </Flex>
                        }
                    />
                ) : null}
            </>
        );
    }
);
